import React from 'react';
import { useSelector } from 'react-redux';

import { getMarket } from '@travel/i18n';
import { Spot } from '@travel/icons/ui';
import { MOBILE_SCREEN, TABLET_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import { IconText } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import Money from 'components/Money';
import ProviderHeaderBox, {
  BookmarkButton,
  ProviderReview,
  ProviderTitle,
} from 'pages/ProviderListPage/components/ProviderBox/ProviderHeaderBox';

import { getLocation } from 'store/__router/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';

import { Translate } from 'core/translate';
import { Link } from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';
import { ProviderItem, SearchFormQuery } from 'ProviderList-Types';

import styles from './providerBox.module.scss';

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  /** Object of provider data */
  item: ProviderItem;
  /** Object of current page query */
  query?: SearchFormQuery;
  areaName: string;
};

function ProviderBox(props: Props) {
  const { item, areaName } = props;

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isTablet = deviceType === TABLET_SCREEN;

  const market = useSelector(getMarket).marketCode;
  const location = useSelector(getLocation);

  const isNotEmptyMedia = isNotEmptyArray(item.media);
  const media = isNotEmptyMedia ? item.media[0] : null;
  const limitations = useSelector(getLimitationsForCurrentMarket);

  const linkPath = `${paths.providerInfo.pathResolver(item.pathId)}${
    location?.search
      ? `?${toSEOSearchString(
          {
            startDate: props.query?.startDate,
            endDate: props.query?.endDate,
            noOfUnits: props.query?.noOfUnits,
            adults: props.query?.adults,
            childrenAges: props.query?.childrenAges,
            promotionCode: props.query?.promotionCode,
            isCouponSpecified: props.query?.isCouponSpecified,
            salesPromotionId: props.query?.salesPromotionId,
          },
          limitations,
        )}`
      : ''
  }`;

  return (
    <div className={styles.container}>
      <Link
        to={linkPath}
        className={styles.link}
        data-testid={`similar-provider-providerBox-link-${props.item.id}`}
      >
        <div className={styles.itemBoxWrapper}>
          <div className={styles.itemDetails}>
            <div className={styles.providerMediaWrapper}>
              <Media
                wrapperClassName={cx(styles.galleryDetails, styles.mediaWrapper)}
                media={media}
                sizes={isMobile ? '40vw' : isTablet ? '10vw' : '15vw'}
              />
            </div>
            <div className={styles.infoBox}>
              <BookmarkButton
                className={cx(styles.bookmark, styles.bookmarkButtonStyle)}
                providerId={item.id}
                isBookmarked={item.isBookmarked ?? false}
              />
              <ProviderHeaderBox item={item} market={market} className={styles.headerBox} />
              <ProviderTitle item={item} className={styles.title} />
              <ProviderReview item={item} market={market} className={styles.review} />
              <div className={styles.address}>
                {areaName && (
                  <IconText
                    className={styles.address}
                    text={areaName}
                    spaceBetween={5}
                    icon={<Spot size={18} />}
                  />
                )}
              </div>
              <div className={styles.priceLabel}>
                <Translate id="Guide_User_Inventory.Item_Starting_At" />
                <div className={styles.price}>
                  <Money value={item.price?.base} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default ProviderBox;
